import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { walletApi } from '../utils/api';
import { TrendingUp, Zap, Clock, DollarSign } from 'lucide-react';

interface Transaction {
  id: number;
  wallet: string;
  before: string;
  after: string;
  memo: string;
  createtime: number;
}

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const logsRes = await walletApi.getLogs(1, 'all');
        console.log('logsRes:', logsRes);

        if (logsRes.code === 1 && logsRes.data) {
          // 后端返回 { wallet_logs: [...], income: number, expend: number }
          const walletLogs = logsRes.data.wallet_logs || logsRes.data;
          setTransactions(Array.isArray(walletLogs) ? walletLogs.slice(0, 5) : []);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
      setLoading(false);
    };

    fetchData();
  }, []);

  console.log('user:', user);
  console.log('transactions:', transactions);

  // 计算统计数据
  const totalIncome = transactions
    .filter(t => parseFloat(t.wallet) > 0)
    .reduce((sum, t) => sum + parseFloat(t.wallet), 0);

  const totalExpense = transactions
    .filter(t => parseFloat(t.wallet) < 0)
    .reduce((sum, t) => sum + Math.abs(parseFloat(t.wallet)), 0);

  const stats = [
    {
      label: 'Current Balance',
      value: user?.usable || user?.money || '0.00',
      icon: DollarSign,
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-400/10',
    },
    {
      label: 'Total Recharge',
      value: totalIncome.toFixed(2),
      icon: Zap,
      color: 'text-blue-400',
      bgColor: 'bg-blue-400/10',
    },
    {
      label: 'Total Consume',
      value: totalExpense.toFixed(2),
      icon: TrendingUp,
      color: 'text-purple-400',
      bgColor: 'bg-purple-400/10',
    },
    {
      label: 'Score',
      value: user?.score?.toString() || '0',
      icon: Clock,
      color: 'text-orange-400',
      bgColor: 'bg-orange-400/10',
    },
  ];

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-zinc-400 mt-1">Welcome back, {user?.nickname || user?.username}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.label}
              className="bg-zinc-900 border border-zinc-800 rounded-xl p-6"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                  <Icon size={20} className={stat.color} />
                </div>
                <span className="text-zinc-400 text-sm">{stat.label}</span>
              </div>
              <div className="text-2xl font-bold text-white">
                {stat.label === 'Current Balance' || stat.label === 'Total Recharge' || stat.label === 'Total Consume'
                  ? `$${stat.value}`
                  : stat.value}
              </div>
            </div>
          );
        })}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Usage Chart Placeholder */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">API Usage (Last 7 Days)</h2>
          <div className="h-48 flex items-end justify-between gap-2 px-4">
            {[65, 45, 80, 55, 90, 70, 85].map((height, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-2">
                <div
                  className="w-full bg-gradient-to-t from-zinc-700 to-zinc-600 rounded-t transition-all hover:from-emerald-600 hover:to-emerald-500"
                  style={{ height: `${height}%` }}
                />
                <span className="text-zinc-500 text-xs">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i]}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Recent Activity</h2>
          {loading ? (
            <div className="text-zinc-400 text-center py-8">Loading...</div>
          ) : transactions.length === 0 ? (
            <div className="text-zinc-400 text-center py-8">No recent activity</div>
          ) : (
            <div className="space-y-3">
              {transactions.map((tx) => (
                <div
                  key={tx.id}
                  className="flex items-center justify-between py-3 border-b border-zinc-800 last:border-0"
                >
                  <div>
                    <div className="text-white text-sm">{tx.memo}</div>
                    <div className="text-zinc-500 text-xs">{formatTime(tx.createtime)}</div>
                  </div>
                  <div
                    className={`font-medium ${
                      parseFloat(tx.wallet) > 0 ? 'text-emerald-400' : 'text-zinc-400'
                    }`}
                  >
                    {parseFloat(tx.wallet) > 0 ? '+' : ''}{tx.wallet}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <a
            href="/admin/top-up"
            className="flex items-center gap-3 p-4 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors"
          >
            <DollarSign className="text-emerald-400" size={24} />
            <div>
              <div className="text-white font-medium">Add Credits</div>
              <div className="text-zinc-400 text-sm">Top up your balance</div>
            </div>
          </a>
          <a
            href="/admin/billing"
            className="flex items-center gap-3 p-4 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors"
          >
            <TrendingUp className="text-blue-400" size={24} />
            <div>
              <div className="text-white font-medium">View Billing</div>
              <div className="text-zinc-400 text-sm">See transaction history</div>
            </div>
          </a>
          <a
            href="#"
            className="flex items-center gap-3 p-4 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors"
          >
            <Zap className="text-purple-400" size={24} />
            <div>
              <div className="text-white font-medium">API Keys</div>
              <div className="text-zinc-400 text-sm">Manage your keys</div>
            </div>
          </a>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;